/**
 * IgcomOrdersService — the stored copy of the IG COM (Shopify) order ledger,
 * in `IGCOM_Orders` on the CRM site.
 *
 * ON THE CRM SITE, not Manufacturing. IG COM is a CRM screen and its list
 * belongs beside B2B_Orders, CRM_Customers and EmbroideryRequests, on the site
 * the app's default `sp` client already points at. It followed that the list
 * takes a CRM-style plain name rather than an `iSyn2_` one, and that it
 * provisions ITSELF (see provision() below) rather than through the
 * Manufacturing site's Migration Valve.
 *
 * WHY STORE IT AT ALL. Every IG COM screen load was a live Shopify read: ten
 * paged GraphQL requests behind one spinner, capped at a window, and gone the
 * moment the tab closed. Mikko, 2026-09-19: "i want to store data so i have
 * analysis later." Trends and repeat buyers are questions about YEARS, and the
 * live call cannot answer them — Shopify hands back the last N days and
 * throttles anyone who asks for more.
 *
 * PSEUDONYMOUS BY CONSTRUCTION, and this is the part to keep. The list holds a
 * customer KEY, the order's facts and its city/province. It holds NO email, NO
 * phone and NO street address — those exist only in Shopify and reach the
 * screen through the live call, already masked server-side for anyone not on
 * IGCOM_PII_USERS (see api/src/shared/igcomPii.ts). A SharePoint list is read
 * by whoever can read the list; the masking decision must not be undone by
 * writing the masked values' originals into it.
 *
 * The key needs care for the same reason. Shopify's own customer id is an
 * opaque GID and stores as-is; a GUEST checkout has no customer record, so the
 * API falls back to the email — and THAT must not land in the list, so it is
 * hashed on the way in. Stated plainly: a hash of an email is not anonymity,
 * it is the absence of a readable address. It is enough to stop the list being
 * a contact export, which is what it is for.
 *
 * CHUNKED, APPEND-ONLY, NEWEST WINS — the shape iSyn2_OrderStatus uses and for
 * the same reasons. A row per order would be thousands of SharePoint rows to
 * read to draw one chart, and a replace-in-place that failed halfway would
 * leave the list holding half of one sync and half of another with no way to
 * tell which half. So a sync writes chunks; the fold applies them oldest-first
 * and the newest answer for an order stands.
 *
 * A sync writes ONLY orders that differ from what is stored (see changedOrders),
 * so re-syncing an unchanged window costs one read and no writes.
 */
import { sp } from "./spClient";
import { isListMissingError } from "./spErrors";
import { assertBackfillTarget } from "./backfillGuard";
import {
  IGCOM_ORDERS_LIST, ORDERS_PER_ROW, pack, unpackChunk,
  type IIgcomStore, type IIgcomStoredOrder,
} from "./igcomOrdersStore";

export * from "./igcomOrdersStore";

/**
 * Every name here ships with the list spec itself, so there is no site where
 * one column exists and another does not — the failure the `*_SELECT` /
 * `*_CORE` pair exists to survive (CLAUDE.md) cannot arise on a list created
 * whole.
 */
const IGCOM_ORDERS_SELECT = "Id,rowsJson,count,fromDate,toDate,syncedBy,syncedAt";

export class IgcomOrdersService {
  private typeName?: string;

  private async type(): Promise<string> {
    if (this.typeName) return this.typeName;
    const r = await sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${IGCOM_ORDERS_LIST}')?$select=ListItemEntityTypeFullName`);
    this.typeName = r.d.ListItemEntityTypeFullName;
    return this.typeName;
  }

  /**
   * Everything on the list, folded to one row per order, newest ordered first.
   *
   * Oldest chunk first so a later sync overrides an earlier one — a refunded
   * order re-synced next week replaces the paid version of itself rather than
   * appearing twice.
   */
  public async readStore(): Promise<IIgcomStore> {
    let rows: Record<string, unknown>[];
    try {
      rows = await sp.getListItems<Record<string, unknown>>(
        IGCOM_ORDERS_LIST, { select: IGCOM_ORDERS_SELECT });
    } catch (e) {
      // Not provisioned yet is a STATE, not an error: the screen explains it
      // and offers to fix it, the way the Embroidery register does. Anything
      // else is a real failure and still throws.
      if (isListMissingError(e)) return { orders: [], syncedAt: "", syncedBy: "", missing: true };
      throw e;
    }

    const at = (r: Record<string, unknown>): string => String(r.syncedAt ?? "");
    const ordered = [...rows].sort(
      (a, b) => at(a).localeCompare(at(b)) || (a.Id as number) - (b.Id as number));

    const byId = new Map<string, IIgcomStoredOrder>();
    let syncedAt = "";
    let syncedBy = "";
    for (const r of ordered) {
      const stamp = at(r);
      if (stamp) { syncedAt = stamp; syncedBy = String(r.syncedBy ?? ""); }
      for (const o of unpackChunk(r.rowsJson)) byId.set(o.id, o);
    }

    const orders = [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return { orders, syncedAt, syncedBy, missing: false };
  }

  /**
   * Write the orders a sync found, in chunks, reporting progress.
   *
   * The caller passes only what changed (changedOrders), so a re-sync of an
   * unchanged window writes nothing at all.
   */
  public async writeOrders(
    orders: readonly IIgcomStoredOrder[],
    syncedBy: string,
    onProgress?: (done: number, total: number) => void,
  ): Promise<number> {
    // Stated once, at the top, where it can be read: this list is on the
    // allow-list ON PURPOSE and the guard is what says so.
    assertBackfillTarget(IGCOM_ORDERS_LIST);
    if (orders.length === 0) return 0;

    const type = await this.type();
    const syncedAt = new Date().toISOString();
    let done = 0;
    onProgress?.(0, orders.length);

    // Oldest first, so a chunk's fromDate/toDate describe a contiguous slice
    // and the list view reads like a timeline rather than a shuffle.
    const byDate = [...orders].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    for (let i = 0; i < byDate.length; i += ORDERS_PER_ROW) {
      const chunk = byDate.slice(i, i + ORDERS_PER_ROW);
      await sp.createItem(IGCOM_ORDERS_LIST, {
        __metadata: { type },
        Title: `${syncedAt.slice(0, 10)} · ${chunk.length}`,
        rowsJson: JSON.stringify(chunk.map(pack)),
        count: chunk.length,
        fromDate: (chunk[0]?.createdAt ?? "").slice(0, 10),
        toDate: (chunk[chunk.length - 1]?.createdAt ?? "").slice(0, 10),
        syncedBy,
        syncedAt,
      });
      done += chunk.length;
      onProgress?.(done, byDate.length);
    }
    return done;
  }

  /**
   * Create the list on the CRM site. Explicit user action only, and only when
   * absent — the same posture as EmbroideryRequestService.provision and the
   * Migration Valve's own provisioning.
   *
   * It is HERE rather than in the Migration Valve because the Valve
   * provisions the MANUFACTURING site's iSyn2_ lists, and this list belongs
   * beside B2B_Orders and CRM_Customers on the CRM site.
   */
  public async provision(onProgress?: (m: string) => void): Promise<void> {
    onProgress?.(`Creating ${IGCOM_ORDERS_LIST}…`);
    await sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: IGCOM_ORDERS_LIST,
        Description:
          "CRM · IG COM — the stored copy of the Shopify webstore's order ledger, so trend and "
          + "repeat-buyer analysis does not re-read Shopify on every screen load. PSEUDONYMOUS ON "
          + "PURPOSE: a customer KEY plus order facts and city/province, and NEVER an email, a phone "
          + "or a street address. Chunked — rowsJson holds a few hundred orders, append-only, newest "
          + "chunk wins per order. Read by the IG COM screen and by nothing else.",
        BaseTemplate: 100,
      }),
    });
    const fields: { name: string; kind: "text" | "note" | "number" | "datetime"; indexed?: boolean }[] = [
      { name: "rowsJson", kind: "note" },
      { name: "count", kind: "number" },
      { name: "fromDate", kind: "text" },
      { name: "toDate", kind: "text" },
      { name: "syncedBy", kind: "text" },
      { name: "syncedAt", kind: "datetime", indexed: true },
    ];
    const KIND = { text: 2, note: 3, datetime: 4, number: 9 };
    const url = `/_api/web/lists/getbytitle('${IGCOM_ORDERS_LIST}')/fields`;
    for (const f of fields) {
      onProgress?.(`Adding ${f.name}…`);
      await sp.raw(url, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.Field" },
          Title: f.name, FieldTypeKind: KIND[f.kind], Required: false,
        }),
      });
      // Indexed at creation, while the list is empty. SharePoint refuses to
      // index a list past the view threshold, and this one grows steadily.
      if (f.indexed) {
        await sp.raw(`${url}/getbytitle('${f.name}')`, {
          method: "POST",
          headers: { "X-HTTP-Method": "MERGE", "If-Match": "*" },
          body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
        });
      }
    }
    this.typeName = undefined;
    onProgress?.(`${IGCOM_ORDERS_LIST} ready (${fields.length} columns)`);
  }
}

export const igcomOrdersService = new IgcomOrdersService();
